/**
 * Точка слоя Автокрафта из «Редактора» → указатель «Навигации» (docs/wayfinding/plan.md §9.1,
 * «Переход из «Редактора» без потерь»).
 *
 * В «Редакторе» генератор выбирается У КАЖДОЙ ТОЧКИ слоя Автокрафта, угол лежит в своём
 * поле слоя, а типов указателей нет вовсе. Указатель же берёт генератор у ТИПА, а угол — из
 * `signOrientation`. Имя точки лежит в поле заголовка слоя, а указатель читает своё название
 * из `signName`, иначе — шаблон карты. Здесь правила, по которым заполненная точка становится
 * указателем, ничего не потеряв: их зовут и клиент (перенос, вставка), и сервер («Сделать
 * слоем указателей»).
 */
import {
  AUTOCRAFT_DATA_PROPERTY,
  AUTOCRAFT_GENERATOR_PROPERTY,
  AUTOCRAFT_ROTATION_PROPERTY,
} from '../types/autocraft.js';
import type { CustomPropertyDef } from '../types/layer.js';
import {
  WAYFINDING_SIGN_ORIENTATION_PROPERTY,
  WAYFINDING_SIGN_OWN_NAME_PROPERTY,
  WAYFINDING_SIGN_TYPE_PROPERTY,
  type WayfindingSignType,
} from '../types/wayfinding.js';
import { DEFAULT_FEATURE_NAME_PROPERTY, readFeatureDisplayName } from './ensureNameCustomProperty.js';
import { readWayfindingSignNumber, readWayfindingSignOwnName } from './wayfindingSignNames.js';
import { readWayfindingSignTypeId } from './wayfindingSignTypes.js';

/**
 * Поля слоя Автокрафта, которые у указателя теряют смысл: генератор задаёт тип, угол —
 * `signOrientation`. В схему слоя указателей они не переезжают.
 */
export const AUTOCRAFT_POINT_ONLY_FIELDS: ReadonlySet<string> = new Set([
  AUTOCRAFT_GENERATOR_PROPERTY,
  AUTOCRAFT_ROTATION_PROPERTY,
]);

/** Генератор, которым собраны данные точки; `null` — точка их не заполняла. */
export function readAutocraftPointGenerator(
  properties: Record<string, unknown> | null | undefined,
): string | null {
  const value = properties?.[AUTOCRAFT_GENERATOR_PROPERTY];
  return typeof value === 'string' && value !== '' ? value : null;
}

function isFilledValue(value: unknown): boolean {
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  return value === true;
}

/**
 * Заполненные поля формы. Форма пишет все поля генератора разом, в том числе пустые, — набор
 * из одних пустот терять нечего. `@поле` — галочка видимости поля, а не значение.
 */
function filledAutocraftKeys(data: unknown): string[] {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return [];
  }
  return Object.entries(data as Record<string, unknown>)
    .filter(([key, value]) => !key.startsWith('@') && isFilledValue(value))
    .map(([key]) => key);
}

/**
 * Сторона указателя хранит ЗАПОЛНЕННЫЕ данные не того генератора, что задан типом. Такие
 * данные нельзя молча заменить умолчаниями — ни открытием указателя, ни выбором конечной
 * точки; «Обновить все макеты» заменяет их только после вопроса.
 *
 * `sideProps` — свойства глазами одной стороны (`readWayfindingSignSideProperties`).
 */
export function holdsForeignAutocraftData(
  sideProps: Record<string, unknown>,
  generatorHash: string,
): boolean {
  if (sideProps[AUTOCRAFT_GENERATOR_PROPERTY] === generatorHash) {
    return false;
  }
  return filledAutocraftKeys(sideProps[AUTOCRAFT_DATA_PROPERTY]).length > 0;
}

/** Сколько полей формы заполнено — для плашки «Макет заполнен генератором …». */
export function countFilledAutocraftValues(data: unknown): number {
  return filledAutocraftKeys(data).length;
}

function readAngle(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Имя, которое «Редактор» даёт точке сам: «Точка 3» при рисовании (`withFeatureName`) и
 * импорте (`persistDefaultImportedVectorFeatureLabels`). Его человек не выбирал.
 */
const GENERATED_POINT_NAME = /^Точка \d+$/;

/**
 * Название, которое человек сам дал точке в «Редакторе»: поле заголовка её слоя, иначе
 * штатные ключи имени — то, что видно в списке слоёв. `''` — имени нет или оно выдано
 * «Редактором» само (`GENERATED_POINT_NAME`).
 *
 * `nameKey` — поле заголовка слоя, из которого точка пришла (`resolveFeatureNameKey`); не
 * известен (вставка чужого GeoJSON) — только штатные ключи.
 */
export function readEditorPointOwnName(
  properties: Record<string, unknown> | null | undefined,
  nameKey: string = DEFAULT_FEATURE_NAME_PROPERTY,
): string {
  const props = properties ?? {};
  const titled = props[nameKey];
  const name =
    typeof titled === 'string' && titled.trim() !== '' ? titled.trim() : (readFeatureDisplayName(props) ?? '');
  return GENERATED_POINT_NAME.test(name) ? '' : name;
}

export interface AutocraftSignAdoptionContext {
  /** Справочник типов карты: тип ищется по генератору точки. */
  signTypes: ReadonlyArray<Pick<WayfindingSignType, 'id' | 'autocraftGeneratorHash'>>;
  /** Тип слоя указателей, куда встаёт точка; `null` — у слоя типа нет. */
  layerSignTypeId: string | null;
  /** Поле заголовка слоя, откуда точка пришла (`readEditorPointOwnName`). */
  nameKey?: string;
}

/**
 * Патч, превращающий точку из «Редактора» (чаще — слоя Автокрафта) в указатель. `null` в
 * значении снимает ключ — так патч читает слияние свойств на сервере. Пусто — доводить нечего
 * (указатель, уже доведённая точка, точка без имени и данных Автокрафта).
 *
 * - Название (✏️ 19.09, Паша): имя, которое человек дал точке в «Редакторе», становится
 *   своим названием указателя (`signName`) и стоит поверх шаблона карты — сбивать его на
 *   шаблон нельзя. Только у точки, которая ещё не была указателем (нет номера): у указателя
 *   пустое своё название — выбор в пользу шаблона, и копия указателя его не теряет. Исходное
 *   поле имени не снимается — в «Редакторе» точка называется как прежде.
 * - Тип: у точки нет своего типа, а генератор её данных не совпадает с генератором типа
 *   слоя — свой тип с ЭТИМ генератором, если он есть в справочнике. Типа нет — точка
 *   остаётся как есть, и плашка чужих данных предложит его создать.
 * - Угол: `Угол поворота` → `signOrientation` (если своего угла ещё нет); старое поле
 *   снимается, иначе два угла расходились бы с первой же правки.
 */
export function autocraftSignAdoptionPatch(
  properties: Record<string, unknown> | null | undefined,
  context: AutocraftSignAdoptionContext,
): Record<string, unknown> {
  const props = properties ?? {};
  const patch: Record<string, unknown> = {};

  if (AUTOCRAFT_ROTATION_PROPERTY in props) {
    const angle = readAngle(props[AUTOCRAFT_ROTATION_PROPERTY]);
    if (angle !== null && readAngle(props[WAYFINDING_SIGN_ORIENTATION_PROPERTY]) === null) {
      patch[WAYFINDING_SIGN_ORIENTATION_PROPERTY] = angle;
    }
    patch[AUTOCRAFT_ROTATION_PROPERTY] = null;
  }

  const generator = readAutocraftPointGenerator(props);
  if (generator && !readWayfindingSignTypeId(props)) {
    const layerType = context.layerSignTypeId
      ? context.signTypes.find((type) => type.id === context.layerSignTypeId)
      : undefined;
    if (layerType?.autocraftGeneratorHash !== generator) {
      const match = context.signTypes.find((type) => type.autocraftGeneratorHash === generator);
      if (match) {
        patch[WAYFINDING_SIGN_TYPE_PROPERTY] = match.id;
      }
    }
  }

  if (readWayfindingSignNumber(props) === null && readWayfindingSignOwnName(props) === '') {
    const name = readEditorPointOwnName(props, context.nameKey);
    if (name !== '') {
      patch[WAYFINDING_SIGN_OWN_NAME_PROPERTY] = name;
    }
  }
  return patch;
}

/**
 * Свойства точки после доводки — для путей, где свойства уходят целиком (вставка, перенос).
 * Нечего доводить — тот же объект.
 */
export function withAutocraftSignAdoption(
  properties: Record<string, unknown>,
  context: AutocraftSignAdoptionContext,
): Record<string, unknown> {
  const patch = autocraftSignAdoptionPatch(properties, context);
  const entries = Object.entries(patch);
  if (entries.length === 0) {
    return properties;
  }
  const next: Record<string, unknown> = { ...properties };
  for (const [key, value] of entries) {
    if (value === null) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}

/** Схема слоя без полей, которые у указателя теряют смысл (`AUTOCRAFT_POINT_ONLY_FIELDS`). */
export function withoutAutocraftPointFields<T extends Pick<CustomPropertyDef, 'name'>>(
  defs: readonly T[] | null | undefined,
): T[] {
  return (defs ?? []).filter((def) => !AUTOCRAFT_POINT_ONLY_FIELDS.has(def.name));
}

/**
 * Поля слоя-источника, которых нет в схеме слоя указателей, — дописываются в конец. Без
 * этого перенесённые значения лежали бы в свойствах, но панель указателя их не показала бы:
 * она рисует поля по схеме своего слоя. `null` — добавлять нечего.
 */
export function mergeSourceFieldsIntoSignSchema<T extends Pick<CustomPropertyDef, 'name'>>(
  targetDefs: readonly T[] | null | undefined,
  sourceDefs: readonly T[] | null | undefined,
): T[] | null {
  const target = [...(targetDefs ?? [])];
  const names = new Set(target.map((def) => def.name));
  let added = false;
  for (const def of sourceDefs ?? []) {
    if (!def || typeof def.name !== 'string' || def.name === '') {
      continue;
    }
    if (names.has(def.name) || AUTOCRAFT_POINT_ONLY_FIELDS.has(def.name)) {
      continue;
    }
    names.add(def.name);
    target.push(def);
    added = true;
  }
  return added ? target : null;
}
